/**
 * @ Author: Housefolios
 * @ Create Time: 2021-12-01 15:08:58
 * @ Modified by: David Helmick
 * @ Modified time: 2025-09-15 13:52:29
 * @ Description: Layouts the page when looking at the proforma. The PropertyPDF component contains the code that actually displays the proforma.
 */

import React, { Component, useState, useEffect } from 'react'
// Centralized lodash imports - much cleaner!
import {
  find,
  filter,
  includes,
  concat,
  get,
  compact,
} from '@/utils/lodash'
import {
  Box,
  FormControl,
  MenuItem,
  InputLabel,
  ListItemText,
  Select,
  List,
  ListItemButton,
  Collapse,
  Button,
  Menu,
  Typography,
} from '@mui/material'
import withSession from '@/housefolios-components/Session/withSession'
import { GET_COMPPROPERTIES, GET_PROPERTY } from '../queries'
import { PageTitle } from '@/layout-components'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import KeyboardArrowDownTwoTone from '@mui/icons-material/KeyboardArrowDownTwoTone'
import { GET_PORTFOLIOS } from '@/housefolios-components/Portfolio/queries'
import { ExpandLess, ExpandMore } from '@mui/icons-material'
import Proforma from '.'
import { proformaTypeLabel } from './proformaTypeLabel'
import SharePDF from './sharePDF'
import UploadProforma from '../UploadProforma'
import { NavLink, useNavigate } from 'react-router'
import { DEFAULT_ANALYTICS } from '@/housefolios-components/Property/fragments'
import { getStrategyName } from '@/utils/stratagies'
import { useParams } from 'react-router'
import { useQuery } from '@apollo/client/react'
import PropertyPDFRefactored from './PropertyPDFRefactored'
import { LoadingSkeleton } from '@/housefolios-components/LoadingSkeleton'

// Function to map marketplaceStrategy to proforma type
const mapMarketplaceStrategyToProformaType = (
  marketplaceStrategy,
) => {
  if (!marketplaceStrategy) return 'buyHold'

  switch (marketplaceStrategy) {
    case 'Astroflip':
      return 'astroflip'
    case 'Wholesale':
      return 'wholesale'
    case 'Turnkey End Buyer':
      return 'marketing'
    case 'Fix & Flip':
      return 'fixFlip'
    case 'Short Term Rental':
      return 'shortTermRental'
    case 'Buy & Hold':
      return 'buyHold'
    case 'Build to Rent':
      return 'marketing' // Build to Rent maps to marketing type
    case 'BRRRR':
      return 'buyHold' // BRRRR typically uses buyHold proforma
    case 'BOR':
      return 'buyHold' // BOR typically uses buyHold proforma
    default:
      return 'buyHold' // Fallback to buyHold for unknown strategies
  }
}

const PDFContainer = (props) => {
  const { id = '', type } = useParams()

  // Determine initial type: use URL param if provided, otherwise derive from property data
  const getInitialType = () => {
    if (type) return type
    // If no type in URL, we'll set this after property data loads
    return 'publicMarketplace' // Default fallback
  }

  const [currentType, setCurrentType] = useState(getInitialType())
  const [anchorElActionsMenu, setAnchorElActionsMenu] = useState(null)
  const [openActionsMenu, setOpenActionsMenu] = useState(false)
  const [openEmailProforma, setOpenEmailProforma] = useState(false)

  let navigate = useNavigate()

  const onChangeContact = (event) => {
    const { value } = event.target
    setCurrentType(value)
    if (value) navigate(`/property-details/${id}/proforma/${value}`)
  }

  const handleClickActionsMenu = (event) => {
    setAnchorElActionsMenu(event.currentTarget)
  }
  const handleCloseActionsMenu = () => {
    setAnchorElActionsMenu(null)
  }
  const handleActionsClick = () => {
    setOpenActionsMenu(!openActionsMenu)
  }

  const handleEmailProforma = () => {
    setOpenEmailProforma(!openEmailProforma)
  }

  const {
    loading: propertyLoading,
    error: propertyError,
    data: propertyData,
  } = useQuery(GET_PROPERTY, {
    variables: {
      id,
    },
  })

  const {
    loading: portfolioLoading,
    error: portfolioError,
    data: portfolioData,
  } = useQuery(GET_PORTFOLIOS, {
    skip: props.public,
  })

  const {
    loading: compLoading,
    error: compError,
    data: compData,
  } = useQuery(GET_COMPPROPERTIES, {
    variables: {
      propertyId: id,
    },
    skip: !id,
  })

  // Update currentType based on property's marketplaceStrategy when no type URL param is provided
  useEffect(() => {
    if (!type && propertyData?.property) {
      const { property } = propertyData
      const derivedType = mapMarketplaceStrategyToProformaType(
        property.marketplaceStrategy,
      )
      setCurrentType(derivedType)
    }
  }, [type, propertyData])

  if (
    propertyLoading ||
    (!props.public && portfolioLoading) ||
    compLoading
  )
    return <LoadingSkeleton variant="proforma" />
  if (propertyError) return `Error! ${propertyError.message}`
  // if (portfolioError) return `Error! ${portfolioError.message}`
  if (compError) return `Error! ${compError.message}`

  const { property } = propertyData || {}
  const { acquisition = {} } = property || {}
  const { analytics = DEFAULT_ANALYTICS } = acquisition || {}

  const { portfolios = [] } = portfolioData || {}
  let propertyAccess = find(
    compact(
      concat(
        portfolios,
        filter(
          props.session.me?.activeOrg.sharedPortfolios,
          (portfolio) => portfolio.sharePermission === 'canEdit',
        ),
      ),
    ),
    (portfolio) => portfolio?._id === get(property, 'portfolio._id'),
  )

  let shared = false
  if (!propertyAccess) {
    shared = true
    propertyAccess = Boolean(
      find(
        props.session.me?.activeOrg.sharedPortfolios,
        (portfolio) =>
          portfolio._id === get(property, 'portfolio._id'),
      ) ||
      find(
        props.session.me?.activeOrg.sharedProperties,
        (propertyId) => propertyId === id,
      ),
    )
  }

  const { compProperties = [] } = compData || {}
  const determinedComps = filter(
    compProperties,
    (comp) => !comp.potential,
  )

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        minHeight: 0,
        width: '100%',
        flex: '1 1 auto',
      }}
    >
      {!props.public && (
        <PageTitle
          titleHeading="Proforma"
          titleDescription="Show off the property"
          style={{ margin: 0, flexShrink: 0 }}
          icon={
          <FontAwesomeIcon
          style={{ fontSize: 30 }}
          icon={['far', 'file-lines']}
          sx={{ color: '#368cff' }}
          />
          }
          >
          {!shared && (
            <Button
              variant="contained"
              color="primary"
              onClick={handleClickActionsMenu}
              style={{
                marginRight: 5,
                paddingLeft: 35,
                paddingRight: 30,
              }}
            >
              <Typography>Actions</Typography>
              <KeyboardArrowDownTwoTone />
            </Button>
          )}
          <Menu
            anchorEl={anchorElActionsMenu}
            keepMounted
            open={Boolean(anchorElActionsMenu)}
            onClose={handleCloseActionsMenu}
            slotProps={{
              list: { sx: { py: 1 } },
            }}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'center',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'center',
            }}
          >
            <Box sx={{ minWidth: "22rem" }}>
              <List
                component="div"
                sx={{
                  "& .MuiListItem-root.MuiListItem-button": { borderRadius: 0 },
                  "& .MuiListItem-root.MuiListItem-button.Mui-selected, & .MuiListItem-root.MuiListItem-button:hover, & .MuiListItemButton-root.Mui-selected, & .MuiListItemButton-root:hover": {
                    color: "#368cff",
                    background: "rgba(54, 140, 255, 0.05)",
                    "&::before": { background: "#368cff" },
                  },
                }}
              >
                {props.session.me?.activeOrg.member !== 'RWN' && (
                  <ListItemButton
                    key="Report"
                    onClick={handleActionsClick}
                    sx={[{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "flex-start",
                      width: "100%",
                      cursor: "pointer",
                      columnGap: "8px",
                      "& > .MuiSvgIcon-root": { marginLeft: "auto", flexShrink: 0 },
                    }, { py: 1 }]}
                  >
                    <Box sx={{
                      display: "flex",
                      alignItems: "center",
                      columnGap: "8px",
                      minWidth: 0,
                      flex: "1 1 auto",
                      justifyContent: "flex-start",
                      "& > .MuiBox-root:first-of-type": {
                        flex: "0 0 auto",
                        width: 16,
                        minWidth: 16,
                        marginRight: 0,
                      },
                    }}>
                      <Box sx={{
                        width: 16,
                        minWidth: 16,
                        flexShrink: 0,
                        textAlign: "center",
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        marginRight: 10,
                        fontSize: "1.15rem",
                        "& > svg": { width: "1em", height: "1em" },
                        ".Mui-selected > &": { opacity: "1 !important" },
                      }}>
                        <FontAwesomeIcon icon={['far', 'print']} />
                      </Box>
                      <span>View Report</span>
                    </Box>
                    {openActionsMenu ? (
                      <ExpandLess />
                    ) : (
                      <ExpandMore />
                    )}
                  </ListItemButton>
                )}
                <Collapse
                  in={openActionsMenu}
                  timeout="auto"
                  unmountOnExit
                >
                  <List component="div" disablePadding>
                    <ListItemButton
                      component="a"
                      href={`/property-details/${property._id}/proforma/buyHold`}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "flex-start",
                        width: "100%",
                        cursor: "pointer",
                        columnGap: "8px",
                        "& > .MuiSvgIcon-root": { marginLeft: "auto", flexShrink: 0 },
                        pl: 3,
                        color: "inherit",
                        textDecoration: "none",
                      }}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <Box sx={{
                        display: "flex",
                        alignItems: "center",
                        columnGap: "8px",
                        minWidth: 0,
                        flex: "1 1 auto",
                        justifyContent: "flex-start",
                        "& > .MuiBox-root:first-of-type": {
                          flex: "0 0 auto",
                          width: 16,
                          minWidth: 16,
                          marginRight: 0,
                        },
                      }}>
                        <Box sx={{
                          width: 16,
                          minWidth: 16,
                          flexShrink: 0,
                          textAlign: "center",
                          display: "inline-flex",
                          alignItems: "center",
                          justifyContent: "center",
                          marginRight: 10,
                          fontSize: "1.15rem",
                          "& > svg": { width: "1em", height: "1em" },
                          ".Mui-selected > &": { opacity: "1 !important" },
                        }}>
                          <FontAwesomeIcon
                            icon={['far', 'file-lines']}
                          />
                        </Box>
                        <span>Proforma</span>
                      </Box>
                    </ListItemButton>
                    <PropertyPDFRefactored
                      property={property}
                      propertyId={property._id}
                      editProforma={false}
                      handleShareClose={handleCloseActionsMenu}
                      CMA={true}
                      type="CMA"
                      download={true}
                      comps={determinedComps}
                    />
                    {property.proformaFile && (
                      <PropertyPDFRefactored
                        property={property}
                        propertyId={property._id}
                        editProforma={false}
                        handleShareClose={handleCloseActionsMenu}
                        type="uploaded"
                        uploaded={true}
                        download={true}
                      />
                    )}
                  </List>
                </Collapse>
                <Proforma
                  property={property}
                  images={property.images}
                  propertyId={property._id}
                  description={property.description}
                  proformaDescription={property.proformaDescription}
                  proformaImages={property.proformaImages}
                  proformaImagePage={property.proformaImagePage}
                  proformaLogo={property.proformaLogo}
                  proformaRehabCost={property.proformaRehabCost}
                  proformaHideAddress={property.proformaHideAddress}
                  proformaContact={property.proformaContact}
                  analytics={analytics}
                  type={currentType}
                />
                <PropertyPDFRefactored
                  property={property}
                  propertyId={property._id}
                  editProforma={false}
                  handleShareClose={handleCloseActionsMenu}
                  comps={determinedComps}
                  type={type}
                />
                <SharePDF
                  property={property}
                  propertyId={property._id}
                  handleShareClose={handleCloseActionsMenu}
                  type={currentType}
                />
                <UploadProforma
                  ActionsButton={true}
                  proformaFile={property.proformaFile}
                  propertyId={property._id}
                />
              </List>
            </Box>
          </Menu>
          {!shared && (
            <FormControl
              variant="outlined"
              size="small"
              sx={{ minWidth: 240, ml: 1 }}
            >
              <InputLabel>{'Proforma type:'}</InputLabel>
              <Select
                name="type"
                value={currentType}
                onChange={(event) => onChangeContact(event)}
                label="Proforma type:"
                renderValue={(value) =>
                  proformaTypeLabel(value, props.session)
                }
                inputProps={{
                  name: 'type',
                }}
              >
                <MenuItem value={'buyHold'}>
                  <ListItemText
                    primary={getStrategyName(
                      'Buy & Hold',
                      props?.session?.me?.activeOrg?.member,
                    )}
                  />
                </MenuItem>
                <MenuItem value={'fixFlip'}>
                  <ListItemText
                    primary={getStrategyName(
                      'Fix & Flip',
                      props?.session?.me?.activeOrg?.member,
                    )}
                  />
                </MenuItem>
                <MenuItem value={'marketing'}>
                  <ListItemText
                    primary={
                      props.session.me?.activeOrg.southernImpressions
                        ? getStrategyName(
                            'Build to Rent',
                            props?.session?.me?.activeOrg?.member,
                          )
                        : 'End Buyer'
                    }
                  />
                </MenuItem>
                <MenuItem value={'shortTermRental'}>
                  <ListItemText
                    primary={getStrategyName(
                      'Short Term Rental',
                      props?.session?.me?.activeOrg?.member,
                    )}
                  />
                </MenuItem>
                {props?.session?.me?.activeOrg?.member !==
                  'astroflip' && (
                  <MenuItem value={'wholesale'}>
                    <ListItemText
                      primary={getStrategyName(
                        'Wholesale',
                        props?.session?.me?.activeOrg?.member,
                      )}
                    />
                  </MenuItem>
                )}
                {props?.session?.me?.activeOrg?.member ===
                  'astroflip' && (
                  <MenuItem value={'astroflip'}>
                    <ListItemText
                      primary={getStrategyName(
                        'Astroflip',
                        props?.session?.me?.activeOrg?.member,
                      )}
                    />
                  </MenuItem>
                )}
              </Select>
            </FormControl>
          )}
        </PageTitle>
      )}
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {!props.public &&
          !includes(window.location.href, 'property-details') && (
          <Box sx={{ textAlign: 'center' }}>
            <NavLink to="/signin">
              <img
                src="/images/NewLogoBlue.png"
                alt="Housefolios"
                style={{ height: 40 }}
              />
            </NavLink>
          </Box>
        )}
        <Box sx={{ flex: 1, minHeight: 0, width: '100%' }}>
          <PropertyPDFRefactored
            property={property}
            propertyId={id}
            type={currentType}
            display={true}
          />
        </Box>
      </Box>
    </Box>
  )
}

export default withSession(PDFContainer)
